import { opisWyboru } from './rynek'

/**
 * Sprzęt, który klient już wybrał (karty `components/rynek/SprzetKlienta`), zamieniony na pola opisu zlecenia. Czyste
 * funkcje wspólne dla formularza zlecenia i prostego doboru (`lib/doborCele.szkicDoboru`).
 *
 * Stan: `{ panel, liczbaModulow, falownik, magazyn, uwagi: { moduly, falownik, magazyn } }`, gdzie `panel`, `falownik`
 * i `magazyn` to wartości `WyborSprzetu`.
 */

export const MAKS_UWAG_SPRZETU = 50
export const PUSTY_SPRZET = { panel: null, liczbaModulow: '', falownik: null, magazyn: null, uwagi: { moduly: '', falownik: '', magazyn: '' } }

/** Marka z wyboru sprzętu (`WyborSprzetu`: producent z list albo z konkretnego modelu). */
const marka = (w) => w?.producent || w?.model?.producent || null

const liczba = (v) => {
  const n = Number(String(v ?? '').replace(',', '.').replace(/\s/g, ''))
  return String(v ?? '').trim() && Number.isFinite(n) && n >= 0 ? n : null
}

/**
 * Element sprzętu do opisu zlecenia (uwagi przeglądu H6). Konkretny model z katalogu to wzór dla wykonawców
 * (`<rodzaj>`, `<rodzaj>_model_id`; zamiennik tylko z powodem, backend `przetarg.zestaw_zlecenia`). Bez modelu to
 * preferencje klienta: zawężenie z list (`<rodzaj>_preferencja`, np. „Huawei · SUN2000”) i słowa (`<rodzaj>_uwagi`).
 * Marka zawsze osobno (`<rodzaj>_marka`): dobór wykonawców po markach, które montują (kontrakt, sekcja 4 planu).
 */
export const elementSprzetu = (rodzaj, w, jednostka, uwagi) => {
  const model = w?.model?.id != null ? w.model : null
  return {
    [rodzaj]: model ? opisWyboru(w, jednostka) : null,
    [`${rodzaj}_model_id`]: model && Number.isInteger(Number(model.id)) ? Number(model.id) : null,
    [`${rodzaj}_preferencja`]: model ? null : opisWyboru(w, jednostka),
    [`${rodzaj}_marka`]: marka(w),
    [`${rodzaj}_uwagi`]: String(uwagi || '').trim().slice(0, MAKS_UWAG_SPRZETU) || null,
  }
}

/** Pola opisu zlecenia ze sprzętu klienta (te same klucze co backend `installers/notify.POLA_OPISU` i Rynek). */
export const opisSprzetuKlienta = (s) => {
  const st = { ...PUSTY_SPRZET, ...(s || {}), uwagi: { ...PUSTY_SPRZET.uwagi, ...(s?.uwagi || {}) } }
  const sztuk = liczba(st.liczbaModulow)
  const mocModulu = st.panel?.model?.moc_w ?? (st.panel?.moc ? Number(st.panel.moc) : null)
  return {
    ...elementSprzetu('moduly', st.panel, 'W', st.uwagi.moduly),
    liczba_modulow: sztuk,
    kwp: sztuk && mocModulu ? Math.round(sztuk * mocModulu / 10) / 100 : null,
    ...elementSprzetu('falownik', st.falownik, 'kW', st.uwagi.falownik),
    falownik_kw: st.falownik?.model && st.falownik.moc ? Number(st.falownik.moc) || null : null,
    ...elementSprzetu('magazyn', st.magazyn, 'kWh', st.uwagi.magazyn),
    magazyn_kwh: st.magazyn?.model?.pojemnosc_kwh ?? (st.magazyn?.moc ? Number(st.magazyn.moc) : null),
  }
}

/** Czy klient podał choć jedną informację o sprzęcie (producent, model albo kilka słów). */
export const maOpisSprzetu = (s) => [[s?.panel, s?.uwagi?.moduly], [s?.falownik, s?.uwagi?.falownik], [s?.magazyn, s?.uwagi?.magazyn]]
  .some(([w, u]) => opisWyboru(w, '') || String(u || '').trim())
